import { useEffect, useState } from "react";
import { scroller } from "react-scroll";
import { AppId, FCX, ProductLogo, Tooltip, usePlatformContext, usePlatformFeatureFlag } from "@datassential/platform-ui-lib";
import Page from "@containers/Page/Page";
import PageContent from "@containers/PageContent/PageContent";
import MacsHeader from "./MacsHeader";
import FastestGrowing from "@pages/TrendingNow/FastestGrowing";
import ReadAboutIt from "@pages/TrendingNow/ReadAboutIt";
import PeopleSocialPublication from "@pages/TrendingNow/PeopleSocialPublication";
import MacsListSwitch from "./MacsListSwitch/MacsListSwitch";
import { CustomBaseScrollbars } from "@components/CustomScrollbarsVirtualList/CustomScrollbarsVirtualList";
import { getSectionId } from "@core/helpers";
import useFavicon from "@core/hooks/useFavicon";
import './TrendingNow.scss';
import HeaderInfo from "@pages/TrendingNow/HeaderInfo";
import PeopleSaying from "@pages/TrendingNow/PeopleSaying";
import TrendingNowHeader, { TrendingNowTab } from "@pages/TrendingNow/TrendingNowHeader";
import TrendingNowSocialPosts from "@pages/TrendingNow/TrendingNowSocialPosts";
import FoodiesSaying from "@pages/TrendingNow/FoodiesSaying/FoodiesSaying";
import InfluencersSaying from "@pages/TrendingNow/InfluencersSaying/InfluencersSaying";

export const TRENDING_NOW_SCROLLBAR_ID = 'TrendingNowPageScrollbar';

const TrendingNow: FCX = () => {
    const [isScrolled, setIsScrolled] = useState(false);
    const { isUserInfoLoaded } = usePlatformContext();
    const isInfluencersEnabled = usePlatformFeatureFlag('Influencers release');
    useFavicon('/trending-now-favicon.ico');

    const tabs: TrendingNowTab[] = [
        { id: 'top-posts', title: 'Top Posts' },
        { id: 'people', title: 'People' },
        { id: 'foodies', title: isInfluencersEnabled ? 'Influencers' : 'Foodies' },
        { id: 'mac', title: 'MAC' },
    ];

    const navigationOffset = 82;

    useEffect(() => {
        const hash = window.location.hash?.replace('#', '');
        if (hash) {
            setTimeout(() => {
                scroller.scrollTo(hash, {
                    containerId: TRENDING_NOW_SCROLLBAR_ID,
                    smooth: true,
                    duration: 500,
                    offset: -navigationOffset,
                });
            }, 500);
        }
    }, [navigationOffset]);

    return (
        /* @ts-ignore */
        <Page className="TrendingNow">
            <PageContent>
                <CustomBaseScrollbars
                    id={TRENDING_NOW_SCROLLBAR_ID}
                    onScroll={(event: any) => {
                        setIsScrolled(event?.target?.scrollTop > 0);
                    }}
                >
                    <TrendingNowHeader
                        tabs={tabs}
                        containerId={TRENDING_NOW_SCROLLBAR_ID}
                        offset={navigationOffset}
                        isScrolled={isScrolled}
                    />
                    <div className="TrendingNow__content" data-testid="blockMacsPage">
                        <div id={getSectionId('top-posts')} className="TrendingNow__sectionBlock">
                            <div className="TrendingNow__title">SOCIAL
                                <div className="divider"/>
                            </div>
                            <div className="TrendingNow__wrapper">
                                <div className="TrendingNow__section">
                                    <HeaderInfo
                                        text="top food & beverage posts"
                                        infoTooltipId="top-food-beverage-posts"
                                    />
                                    <Tooltip id="top-food-beverage-posts" place="right" style={{ zIndex: 1 }}>
                                        Recent top food and beverage related posts based on
                                        <br/>
                                        a blend of follower count and viral engagement level
                                    </Tooltip>
                                </div>
                                <div className="TrendingNow__section">
                                    <TrendingNowSocialPosts/>
                                </div>
                            </div>
                        </div>
                        <div id={getSectionId('people')} className="TrendingNow__sectionBlock">
                            <div className="TrendingNow__wrapper">
                                <div className="TrendingNow__section">
                                    <HeaderInfo
                                        text="WHAT PEOPLE ARE SAYING"
                                        highlightWord="PEOPLE"
                                        infoTooltipId="what-people-saying-info"
                                    />
                                    <Tooltip id="what-people-saying-info" place="bottom" className="TrendingNow__tooltip">
                                        Fastest growing keywords by social media engagement;<br/><b>engagement</b> = sum of likes, comments, and shares. These items saw the highest increases in social media activity in the last month.
                                    </Tooltip>
                                    <span className="TrendingNow__subheader">past 1-month growth in social media engagement</span>
                                </div>
                                <div className="TrendingNow__section" data-testid="blockPeopleSayingSection">
                                    <PeopleSaying/>
                                    <PeopleSocialPublication/>
                                </div>
                            </div>
                        </div>
                        <div className="TrendingNow__socialListening">
                            <div id={getSectionId('foodies')} className="TrendingNow__sectionBlock">
                                {!isUserInfoLoaded ? (
                                    <div className="TrendingNow__wrapper">
                                        <div className="TrendingNow__spinner">
                                            <ProductLogo appId={AppId.Snap} type="regular"/>
                                        </div>
                                    </div>
                                ) : isInfluencersEnabled ? (
                                    <InfluencersSaying/>
                                ) : (
                                    <div className="TrendingNow__wrapper">
                                        <div className="TrendingNow__section">
                                            <HeaderInfo
                                                text="WHAT FOODIES ARE SAYING"
                                                highlightWord="FOODIES"
                                                infoTooltipId="what-foodies-saying-info"
                                                highlightColor="#CF12FF"
                                            />
                                            <Tooltip id="what-foodies-saying-info" place="right">
                                                An expert-curated panel of 1000+ chefs, influencers, home cooks, food lovers, and more
                                            </Tooltip>
                                        </div>
                                        <div className="TrendingNow__section" data-testid="blockFoodiesSayingSection">
                                            <FoodiesSaying/>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div id={getSectionId('mac')} className="TrendingNow__sectionBlock">
                            <div className="TrendingNow__title">MENU
                                <div className="divider"/>
                            </div>
                            <div className="TrendingNow__wrapper">
                                <div className="TrendingNow__section">
                                    <MacsHeader/>
                                </div>
                                <div className="TrendingNow__section" data-testid="blockMacsWhatsHotSection">
                                    <FastestGrowing/>
                                    <ReadAboutIt/>
                                </div>
                                <div className="TrendingNow__section TrendingNowTableSection">
                                    <MacsListSwitch/>
                                </div>
                            </div>
                        </div>
                    </div>
                </CustomBaseScrollbars>
            </PageContent>
        </Page>
    );
};

export default TrendingNow;
